// React UI: Casbin Entitlement Admin Panel with Radio Selection & Role Dropdown

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  InputLabel
} from '@mui/material';
import PolicyIcon from '@mui/icons-material/Policy';
import callAuthApi from '../../common/AuthAPIClient';

export default function ManageEntitlements() {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('');
  const [object, setObject] = useState('');
  const [action, setAction] = useState('');
  const [entitlements, setEntitlements] = useState(null);
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('assignRole');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  useEffect(() => {
    const fetchRolesAndUsers = async () => {
      try {
        const roleRes  = await callAuthApi({
          url: "/roles",
          method: "GET",
          params: {}
        });
        setAvailableRoles(roleRes.roles || []);

        const userRes = await callAuthApi({
          url: "/users",
          method: "GET",
          params: {}
        });
        setAvailableUsers(userRes.users || []);
      } catch (err) {
        console.error('Failed to fetch roles');
      }
    };
    fetchRolesAndUsers();
  }, []);

  const handleAssignRole = async () => {
    try {
      const res = await callAuthApi({
        url: "/assign-role",
        method: "POST",
        data: { user_id: userId, role }
      });

      setMessage(res.status);
    } catch (err) {
      setMessage('❌ Error assigning role');
    }
  };

  const handleAssignPermission = async () => {
    try {
      const res = await callAuthApi({
        url: "/assign-permission",
        method: "POST",
        data: { role, object, act: action }
      });

      setMessage(res.status);
    } catch (err) {
      setMessage('❌ Error assigning permission');
    }
  };

  const handleGetEntitlements = async () => {
    try {
      const res = await callAuthApi({
        url: "/entitlements/${" + userId + "}",
        method: "GET",
        params: {}
      });
      setEntitlements(res.data);
      setMessage('✅ Entitlements loaded');
    } catch (err) {
      setMessage('❌ Error retrieving entitlements');
    }
  };

  const handleSubmit = () => {
    if (mode === 'assignRole') return handleAssignRole();
    if (mode === 'assignPermission') return handleAssignPermission();
    if (mode === 'getEntitlements') return handleGetEntitlements();
  };

  return (
    <Container maxWidth="md" sx={{ paddingTop: 4 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <PolicyIcon color="primary" />
        <Typography variant="h5">Entitlements Admin Panel</Typography>
      </Box>

      <Paper sx={{ padding: 3, mb: 4 }} elevation={3}>
        <Stack spacing={3}>
          <FormControl>
            <FormLabel>Action</FormLabel>
            <RadioGroup
              row
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <FormControlLabel value="assignRole" control={<Radio />} label="Assign Role to User" />
              <FormControlLabel value="assignPermission" control={<Radio />} label="Assign Permission to Role" />
              <FormControlLabel value="getEntitlements" control={<Radio />} label="Get User Entitlements" />
            </RadioGroup>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Select User</InputLabel>
            <Select value={userId} label="Select User" onChange={(e) => setUserId(e.target.value)}>
              {availableUsers.map((u, i) => (
                <MenuItem key={i} value={u.email}>
                  {u.email} ({u.uid})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(mode === 'assignRole' || mode === 'assignPermission') && (
            <FormControl fullWidth>
              <InputLabel>Select Role</InputLabel>
              <Select value={role} label="Select Role" onChange={(e) => setRole(e.target.value)}>
                {availableRoles.map((r, i) => (
                  <MenuItem key={i} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {mode === 'assignPermission' && (
            <Box display="flex" gap={2}>
              <TextField
                label="Entitlement Object"
                value={object}
                onChange={(e) => setObject(e.target.value)}
                fullWidth
              />
              <TextField
                label="Action"
                value={action}
                onChange={(e) => setAction(e.target.value)}
                fullWidth
              />
            </Box>
          )}

          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>

          <Divider />

          {entitlements && mode === 'getEntitlements' && (
            <Box>
              <Typography variant="h6" sx={{ mt: 2 }}>Roles:</Typography>
              <List>
                {entitlements.roles.map((r, i) => (
                  <ListItem key={i}><ListItemText primary={r} /></ListItem>
                ))}
              </List>

              <Typography variant="h6">Permissions:</Typography>
              <List>
                {entitlements.permissions.map((p, i) => (
                  <ListItem key={i}>
                    <ListItemText primary={`${p.object} - ${p.action}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {message && (
            <Typography variant="subtitle1" color="primary">
              <strong>Status:</strong> {message}
            </Typography>
          )}
        </Stack>
      </Paper>
    </Container>
  );
}